// betonijerry/src/utils/auth/impersonateApi.js
// betoni.online staff impersonation of a Jerry customer. Reuses the shared
// betoni.online endpoints — there is nothing betonijerry-specific server-side;
// canImpersonate gates it from the caller's JWT (systemAdmin/roleManager).

import { API_URL } from "src/utils/apiUrl";
import { jsonOrThrow, authHeaders } from "src/utils/apiFetch";

const jsonHeaders = (token) => ({ "Content-Type": "application/json", ...authHeaders(token) });

/**
 * Mint a 10-minute impersonation token for the target, then hydrate the
 * target's FULL auth payload via loginWithJwtToken — and keep the imp token as
 * the session token. The backend echoes an imp token back unchanged (fb#1999);
 * the override stays as defence in depth against one that re-mints a 7-day
 * token without imp/imp_sid (banner countdown, extend/end, audit tagging).
 *
 * @returns the target's full auth payload `{ person, personSettings, asiakasList,
 *   asiakasPersonSettings, token }` with `token` overridden to the imp token.
 */
export async function startImpersonation({ token, personId, email }) {
  const mintRes = await fetch(`${API_URL}/api/auth/impersonate`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify(email ? { email } : { personId }),
  });
  const { token: impToken } = await jsonOrThrow(mintRes);

  const hydrateRes = await fetch(`${API_URL}/api/auth/login/loginWithJwtToken`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token: impToken }),
  });
  const payload = await jsonOrThrow(hydrateRes);
  return { ...payload, token: impToken };
}

/**
 * Write the impersonation_end audit row with the active imp token (Path A).
 * endReason ∈ {manual,timeout,error,logout}: "timeout" when the session auto-ends
 * on token expiry, "logout" when the staffer logs out mid-impersonation, "manual"
 * (default) when the staffer clicks Lopeta.
 */
export async function endImpersonationApi({ token, endReason = "manual" }) {
  const res = await fetch(`${API_URL}/api/auth/impersonate/end`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ endReason }),
  });
  return jsonOrThrow(res);
}

/** Renew the impersonation session for 10 more minutes. Returns `{ token }`. */
export async function extendImpersonationApi({ token }) {
  const res = await fetch(`${API_URL}/api/auth/impersonate/extend`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({}),
  });
  return jsonOrThrow(res);
}

// ---------------------------------------------------------------------------
// Impersonation favorites (staff quick-select). The shared betoni.online
// endpoints keep one list per admin PERSON; `context: "betonijerry"` scopes this
// app to its OWN list (personContactTypeId=2) so a staffer's betonijerry targets
// never mix with their betoni.online ones. All three require the admin token
// (systemAdmin/roleManager) — while impersonating, pass the STASHED admin token,
// not the target's imp token (which is not an admin and would 403). AuthContext
// exposes that as `adminToken`.
// ---------------------------------------------------------------------------
const FAVORITES_URL = `${API_URL}/api/auth/impersonate/favorites`;
const FAVORITES_CONTEXT = "betonijerry";

/** List the staffer's saved betonijerry impersonation targets (newest first). */
export async function fetchImpersonationFavorites(token) {
  const res = await fetch(`${FAVORITES_URL}?context=${FAVORITES_CONTEXT}`, {
    headers: authHeaders(token),
  });
  const data = await jsonOrThrow(res);
  return Array.isArray(data) ? data : [];
}

/** Add a person to the betonijerry favorites list (idempotent server-side). */
export async function addImpersonationFavorite({ token, targetPersonId }) {
  const res = await fetch(FAVORITES_URL, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ targetPersonId, context: FAVORITES_CONTEXT }),
  });
  return jsonOrThrow(res);
}

/** Remove a person from the betonijerry favorites list. */
export async function removeImpersonationFavorite({ token, targetPersonId }) {
  const res = await fetch(`${FAVORITES_URL}/${targetPersonId}?context=${FAVORITES_CONTEXT}`, {
    method: "DELETE",
    headers: authHeaders(token),
  });
  return jsonOrThrow(res);
}
